import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Vote, ArrowLeft, Loader2, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { pollsAPI } from "../lib/api";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const pollSchema = z.object({
  title: z.string().min(3, "Titre requis (min 3 caractères)"),
  description: z.string().optional(),
  organizer_name: z.string().min(2, "Nom de l'organisateur requis"),
  organizer_logo: z.string().url().optional().or(z.literal("")),
  max_votes_per_email: z.coerce.number().min(1).default(1),
  end_date: z.string().optional(),
});

export default function CreatePoll() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: zodResolver(pollSchema),
    defaultValues: {
      organizer_name: user?.company_name || "",
      max_votes_per_email: 1,
      organizer_logo: "",
    }
  });

  const watchedLogo = watch("organizer_logo");

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setValue("organizer_logo", res.data.url);
      toast.success("Logo uploadé");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const res = await pollsAPI.create(data);
      toast.success("Sondage créé avec succès");
      navigate(`/polls/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-4">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
              <Vote className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit' }}>Nouveau sondage</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Card className="bg-card">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }}>Informations du sondage</CardTitle>
            <CardDescription>
              Configurez les informations de base de votre sondage ou vote.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="title">Titre du sondage *</Label>
                  <Input
                    id="title"
                    placeholder="Ex: Élection du nouveau logo"
                    className="h-11 bg-slate-50 border-transparent focus:border-primary"
                    data-testid="poll-title-input"
                    {...register("title")}
                  />
                  {errors.title && (
                    <p className="text-sm text-destructive">{errors.title.message}</p>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Décrivez votre sondage..."
                    className="bg-slate-50 border-transparent focus:border-primary min-h-[100px]"
                    {...register("description")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="organizer_name">Nom de l'organisateur *</Label>
                  <Input
                    id="organizer_name"
                    placeholder="Nom de l'entreprise / association"
                    className="h-11 bg-slate-50 border-transparent focus:border-primary"
                    data-testid="poll-organizer-input"
                    {...register("organizer_name")}
                  />
                  {errors.organizer_name && (
                    <p className="text-sm text-destructive">{errors.organizer_name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_votes_per_email">Votes max par email</Label>
                  <Input
                    id="max_votes_per_email"
                    type="number"
                    min="1"
                    className="h-11 bg-slate-50 border-transparent focus:border-primary"
                    {...register("max_votes_per_email")}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Logo</Label>
                  <input
                    type="file"
                    ref={logoInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleUploadLogo}
                  />
                  <div className="flex items-center gap-4">
                    {watchedLogo ? (
                      <div className="relative group">
                        <img 
                          src={watchedLogo} 
                          alt="Logo" 
                          className="h-12 object-contain rounded-lg bg-slate-100 px-2"
                        />
                        <button
                          type="button"
                          onClick={() => setValue("organizer_logo", "")}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="h-11"
                      >
                        {uploadingLogo ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Uploader un logo
                      </Button>
                    )}
                  </div>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="end_date">Date de fin (optionnel)</Label>
                  <Input
                    id="end_date"
                    type="datetime-local"
                    className="h-11 bg-slate-50 border-transparent focus:border-primary"
                    {...register("end_date")}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <Link to="/dashboard">
                  <Button type="button" variant="outline" className="rounded-full">
                    Annuler
                  </Button>
                </Link>
                <Button 
                  type="submit" 
                  className="rounded-full bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="create-poll-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Création...
                    </>
                  ) : (
                    "Créer le sondage"
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
